import { useEffect } from 'react'

const TITLE_TARGET_SELECTOR = [
  'button',
  'a',
  'img',
  '[role="button"]',
  '[role="menuitem"]',
  '[data-hover-title]',
].join(',')

const MAX_TITLE_LENGTH = 160

const normalizeText = (text) =>
  String(text ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_TITLE_LENGTH)

const getImageFileName = (src = '') => {
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
    return ''
  }

  try {
    const url = new URL(src, window.location.origin)
    const fileName = url.pathname.split('/').filter(Boolean).pop()

    return normalizeText(decodeURIComponent(fileName || ''))
  } catch (error) {
    const fileName = src.split('?')[0].split('#')[0].split('/').filter(Boolean).pop()

    return normalizeText(decodeURIComponent(fileName || ''))
  }
}

const getElementTitle = (element) => {
  if (!element) {
    return ''
  }

  if (element.tagName === 'IMG') {
    return (
      getImageFileName(element.currentSrc || element.src) ||
      normalizeText(element.getAttribute('alt'))
    )
  }

  return (
    normalizeText(element.dataset?.hoverTitle) ||
    normalizeText(element.getAttribute('aria-label')) ||
    normalizeText(element.textContent) ||
    normalizeText(element.getAttribute('value'))
  )
}

const isInsideEditor = (element) =>
  Boolean(element.closest?.('.ProseMirror, [contenteditable="true"]'))

const applyTitle = (element) => {
  if (!element || isInsideEditor(element)) {
    return
  }

  const hasCustomTitle =
    element.hasAttribute('title') && element.dataset.autoHoverTitle !== '1'

  if (hasCustomTitle) {
    return
  }

  const title = getElementTitle(element)

  if (!title) {
    return
  }

  element.setAttribute('title', title)
  element.dataset.autoHoverTitle = '1'
}

const applyTitlesIn = (root) => {
  if (!root?.querySelectorAll) {
    return
  }

  root.querySelectorAll(TITLE_TARGET_SELECTOR).forEach(applyTitle)
}

export const useNativeHoverTitles = () => {
  useEffect(() => {
    applyTitlesIn(document.body)

    const handleHover = (event) => {
      applyTitle(event.target?.closest?.(TITLE_TARGET_SELECTOR))
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return
          }

          if (node.matches?.(TITLE_TARGET_SELECTOR)) {
            applyTitle(node)
          }

          applyTitlesIn(node)
        })
      })
    })

    document.addEventListener('mouseover', handleHover)
    document.addEventListener('focusin', handleHover)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      document.removeEventListener('mouseover', handleHover)
      document.removeEventListener('focusin', handleHover)
      observer.disconnect()
    }
  }, [])
}
